import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Nav,
  Alert,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import Header_Manager from '../../Components/Header_Manager';
import Register_Supervisor from '../../Components/CompanyDetails/Register_Supervisor';
import Register_Agent from '../../Components/CompanyDetails/Register_Agent';
import { BASE_URL } from '../../Config';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Manager.css';

const TABS = {
  COMPANY: 'company',
  SUPERVISOR: 'supervisor',
  AGENT: 'agent',
};

const Manager_Register = () => {
  const [activeTab, setActiveTab] = useState(TABS.COMPANY);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    email: '',
    password: '',
    address: '',
    cnic: '',
    contactNumber: '',
    pocname: '',
    productSale: '',
  });
  const [companyLoading, setCompanyLoading] = useState(false);

  const managerId = JSON.parse(sessionStorage.getItem('user') || '{}')?.userId;

  const loadAssignedCompanies = async () => {
    if (!managerId) return;
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${BASE_URL}AssignedCompany?ManagerId=${managerId}`);
      if (response.data.responseCode === '0000') {
        const list = Array.isArray(response.data.companyDetails)
          ? response.data.companyDetails
          : [];
        setCompanies(list);

        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        sessionStorage.setItem(
          'user',
          JSON.stringify({
            ...user,
            assignedCompanies: list,
          })
        );

        if (!selectedCompanyId && list[0]?.companyProfileId) {
          setSelectedCompanyId(list[0].companyProfileId);
        }
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error(error);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.selectedCompanyId) {
      setSelectedCompanyId(user.selectedCompanyId);
    }
    if (Array.isArray(user.assignedCompanies) && user.assignedCompanies.length > 0) {
      setCompanies(user.assignedCompanies);
    }
    loadAssignedCompanies();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const company = companies.find((c) => c.companyProfileId === selectedCompanyId);
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    sessionStorage.setItem(
      'user',
      JSON.stringify({
        ...user,
        companyId: selectedCompanyId,
        selectedCompanyId,
        selectedCompanyName: company?.name || user.selectedCompanyName || '',
        companyName: company?.name || user.companyName || '',
      })
    );
  }, [selectedCompanyId, companies]);

  const handleCompanyChange = (e) => {
    setCompanyForm({ ...companyForm, [e.target.name]: e.target.value });
  };

  const registerCompany = async (e) => {
    e.preventDefault();

    if (!managerId) {
      Swal.fire('Error', 'Manager session not found. Please sign in again.', 'error');
      return;
    }

    const { companyName, email, password, productSale } = companyForm;
    if (!companyName || !email || !password) {
      Swal.fire('Error', 'Company Name, Email and Password are required.', 'error');
      return;
    }
    if (!productSale) {
      Swal.fire('Error', 'Product Sale is required.', 'error');
      return;
    }

    setCompanyLoading(true);
    try {
      const registerRes = await axios.post(
        `${BASE_URL}Register_Company`,
        {
          ManagerId: managerId,
          CompanyName: companyForm.companyName,
          Email: companyForm.email,
          Password: companyForm.password,
          Address: companyForm.address,
          CNIC: companyForm.cnic,
          ContactNumber: companyForm.contactNumber,
          Pocname: companyForm.pocname,
          ProductSale: companyForm.productSale,
        },
        { headers: { 'Content-Type': 'application/json', accept: '*/*' } }
      );

      if (registerRes.data.responseCode !== '0000') {
        Swal.fire('Error', registerRes.data.message || 'Company registration failed.', 'error');
        return;
      }

      const loginRes = await axios.post(
        `${BASE_URL}Retail_Login`,
        {
          email: companyForm.email,
          password: companyForm.password,
        },
        { headers: { 'Content-Type': 'application/json', accept: '*/*' } }
      );

      if (loginRes.data.responseCode !== '0000') {
        Swal.fire(
          'Warning',
          'Company registered successfully, but automatic login failed. Please select the company manually after it has been assigned.',
          'warning'
        );
        await loadAssignedCompanies();
        return;
      }

      const companyUserId = loginRes.data.userId || loginRes.data.companyId;
      const companyName = loginRes.data.companyName || loginRes.data.name || companyForm.companyName;

      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      sessionStorage.setItem(
        'user',
        JSON.stringify({
          ...user,
          companyId: companyUserId,
          selectedCompanyId: companyUserId,
          selectedCompanyName: companyName,
          companyName,
        })
      );

      setSelectedCompanyId(companyUserId);
      await loadAssignedCompanies();

      Swal.fire({
        icon: 'success',
        title: 'Company Registered',
        text: `${registerRes.data.message || 'Company registered successfully.'} You can now register supervisors and agents for this company.`,
      });

      setCompanyForm({
        companyName: '',
        email: '',
        password: '',
        address: '',
        cnic: '',
        contactNumber: '',
        pocname: '',
        productSale: '',
      });

      setActiveTab(TABS.SUPERVISOR);
    } catch (error) {
      console.error(error);
      Swal.fire('Error', error.response?.data?.message || 'Company registration failed.', 'error');
    } finally {
      setCompanyLoading(false);
    }
  };

  const selectedCompanyName =
    companies.find((c) => c.companyProfileId === selectedCompanyId)?.name || '';

  return (
    <div className="mgr-page">
      <Header_Manager />

      <Container fluid="xl" className="px-3 px-md-4">
        <div className="mgr-hero">
          <h4 className="fw-semibold">Registration Center</h4>
          <p>Follow the sequence: Company → Supervisor → Agent. Register the company first, then add users under that company.</p>
        </div>

        <Card className="mgr-card">
          <Card.Body className="p-3 p-md-4">
            <Nav variant="pills" className="mgr-step-nav gap-2 mb-4">
              <Nav.Item>
                <Nav.Link active={activeTab === TABS.COMPANY} onClick={() => setActiveTab(TABS.COMPANY)}>
                  <i className="bi bi-building me-1" /> 1. Company
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link active={activeTab === TABS.SUPERVISOR} onClick={() => setActiveTab(TABS.SUPERVISOR)}>
                  <i className="bi bi-person-badge me-1" /> 2. Supervisor
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link active={activeTab === TABS.AGENT} onClick={() => setActiveTab(TABS.AGENT)}>
                  <i className="bi bi-person-plus me-1" /> 3. Agent
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {activeTab === TABS.COMPANY && (
              <Form onSubmit={registerCompany}>
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="companyName"
                        name="companyName"
                        placeholder="Company Name"
                        value={companyForm.companyName}
                        onChange={handleCompanyChange}
                        required
                      />
                      <label htmlFor="companyName">Company Name *</label>
                    </Form.Floating>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="email"
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={companyForm.email}
                        onChange={handleCompanyChange}
                        required
                      />
                      <label htmlFor="email">Email *</label>
                    </Form.Floating>
                  </Col>
                  <Col xs={12} md={6}>
                    <InputGroup>
                      <Form.Floating className="flex-grow-1">
                        <Form.Control
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          placeholder="Password"
                          value={companyForm.password}
                          onChange={handleCompanyChange}
                          required
                        />
                        <label htmlFor="password">Password *</label>
                      </Form.Floating>
                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="contactNumber"
                        name="contactNumber"
                        placeholder="Contact Number"
                        value={companyForm.contactNumber}
                        onChange={handleCompanyChange}
                      />
                      <label htmlFor="contactNumber">Contact Number</label>
                    </Form.Floating>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="cnic"
                        name="cnic"
                        placeholder="CNIC"
                        value={companyForm.cnic}
                        onChange={handleCompanyChange}
                      />
                      <label htmlFor="cnic">CNIC</label>
                    </Form.Floating>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="pocname"
                        name="pocname"
                        placeholder="POC Name"
                        value={companyForm.pocname}
                        onChange={handleCompanyChange}
                      />
                      <label htmlFor="pocname">POC Name</label>
                    </Form.Floating>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="productSale"
                        name="productSale"
                        placeholder="Product Sale"
                        value={companyForm.productSale}
                        onChange={handleCompanyChange}
                        required
                      />
                      <label htmlFor="productSale">Product Sale *</label>
                    </Form.Floating>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Floating>
                      <Form.Control
                        id="address"
                        name="address"
                        placeholder="Address"
                        value={companyForm.address}
                        onChange={handleCompanyChange}
                      />
                      <label htmlFor="address">Address</label>
                    </Form.Floating>
                  </Col>
                </Row>

                <div className="d-grid d-md-flex justify-content-md-end mt-4">
                  <Button type="submit" size="lg" className="px-4" disabled={companyLoading}>
                    {companyLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Registering...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2-circle me-2" />
                        Register Company
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}

            {(activeTab === TABS.SUPERVISOR || activeTab === TABS.AGENT) && (
              <>
                <Card className="border-0 bg-light mb-3">
                  <Card.Body className="py-3">
                    <Row className="g-2 align-items-end">
                      <Col xs={12} md={8}>
                        <Form.Label className="fw-semibold mb-1">Select Company</Form.Label>
                        <Form.Select
                          value={selectedCompanyId}
                          onChange={(e) => setSelectedCompanyId(e.target.value)}
                          disabled={loadingCompanies}
                          size="lg"
                        >
                          <option value="">Select Company</option>
                          {companies.map((c) => (
                            <option key={c.companyProfileId} value={c.companyProfileId}>
                              {c.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text muted>
                          Supervisors and agents will be registered under the selected company.
                        </Form.Text>
                      </Col>
                      <Col xs={12} md={4}>
                        <Button
                          variant="outline-secondary"
                          className="w-100"
                          onClick={loadAssignedCompanies}
                          disabled={loadingCompanies}
                        >
                          {loadingCompanies ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" /> Loading
                            </>
                          ) : (
                            <>
                              <i className="bi bi-arrow-clockwise me-1" /> Refresh
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>

                    {selectedCompanyId && selectedCompanyName && (
                      <Alert variant="primary" className="mt-3 mb-0 py-2">
                        Selected: <strong>{selectedCompanyName}</strong>
                      </Alert>
                    )}
                  </Card.Body>
                </Card>

                {!selectedCompanyId ? (
                  <Alert variant="warning" className="mb-0">
                    Please select a company first, then register the{' '}
                    {activeTab === TABS.SUPERVISOR ? 'supervisor' : 'agent'}.
                  </Alert>
                ) : activeTab === TABS.SUPERVISOR ? (
                  <Register_Supervisor
                    companyUserId={selectedCompanyId}
                    companyName={selectedCompanyName}
                    embedded
                  />
                ) : (
                  <Register_Agent
                    key={selectedCompanyId}
                    companyUserId={selectedCompanyId}
                    companyName={selectedCompanyName}
                    embedded
                  />
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Manager_Register;
